import React, { useState, useEffect, useRef } from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    useNodesState,
    useEdgesState,
    Controls,
    Panel,
    useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';
import { cohortLogicToReactFlow } from './cohortLogic';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import {
    Box, Paper, Typography, Button, Menu, MenuItem,
    ListItemIcon, ListItemText, CircularProgress, IconButton, Tooltip, useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// ─── node dimensions & colors ─────────────────────────────────────────────────

const NODE_WIDTH = 220;

const NODE_HEIGHTS = {
    root: 90,
    condition: 90,
    conditionWithCount: 115,
    count: 70,
    final: 80,
};

const HEADER_HEIGHT = 27;
const COUNT_SECTION_HEIGHT = 38;
const CHARS_PER_LINE = 26;  // approx for 220px width @ 12px font
const LINE_HEIGHT = 16;

// Estimate how many lines a condition's body text will wrap to, so tall
// labels (e.g. "Diagnosis: G31 (Other Degenerative Diseases)") get a taller
// box instead of being squeezed against the embedded count section.
const estimateBodyHeight = (text) => {
    const words = String(text || '').split(/\s+/).filter(Boolean);
    let lines = 1;
    let lineLen = 0;
    words.forEach(word => {
        const wordLen = word.length + 1;
        if (lineLen + wordLen > CHARS_PER_LINE) {
            lines += 1;
            lineLen = wordLen;
        } else {
            lineLen += wordLen;
        }
    });
    return Math.max(LINE_HEIGHT * lines + 16, 36);
};

// Node chrome is derived from the palette so the diagram follows light/dark.
const nodeColors = (theme) => {
    const { palette } = theme;
    const isDark = palette.mode === 'dark';
    const rootAccent = isDark ? palette.primary.light : '#4A90E2';
    const countAccent = isDark ? palette.success.light : '#38a169';
    return {
        root: {
            header: rootAccent,
            body: isDark
                ? `linear-gradient(135deg, ${palette.surface.subtle}, ${palette.surface.raised})`
                : 'linear-gradient(135deg, #f5f9ff, #e1ecff)',
            border: rootAccent,
            headerText: isDark ? palette.background.default : '#ffffff',
            bodyText: isDark ? palette.text.primary : '#2c5282',
        },
        condition: {
            header: palette.primary.main,
            body: palette.surface.raised,
            border: palette.primary.main,
            headerText: '#ffffff',
            bodyText: palette.text.primary,
        },
        count: {
            header: countAccent,
            body: isDark ? palette.surface.sunken : '#f0fffe',
            border: isDark ? palette.success.main : '#48bb78',
            headerText: isDark ? palette.background.default : '#ffffff',
            bodyText: isDark ? palette.success.light : '#276749',
        },
        final: {
            header: palette.primary.dark,
            body: `linear-gradient(135deg, ${palette.primary.main}, ${palette.primary.dark})`,
            border: palette.primary.dark,
            headerText: '#ffffff',
            bodyText: '#ffffff',
        },
        edge: isDark ? palette.primary.light : '#4A90E2',
        edgeLabel: palette.text.secondary,
        nodeShadow: isDark ? '0 4px 14px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.10)',
    };
};

// ─── helpers ──────────────────────────────────────────────────────────────────

const detectRole = (node) => {
    if (node.type === 'input') return 'root';
    if (node.type === 'output') return 'final';
    const label = String(node.data?.label || '').trim();
    if (/^(count|final count)[\s:]/i.test(label)) return 'count';
    return 'condition';
};

const buildStyledNode = (node, colors) => {
    const role = detectRole(node);
    const c = colors[role];
    const rawLabel = String(node.data?.label || '');

    const newlineIdx = rawLabel.indexOf('\n');

    // Condition nodes may carry an embedded count line after merging
    let mainText = rawLabel;
    let embeddedCount = null;
    if (role === 'condition' && newlineIdx > -1) {
        mainText = rawLabel.slice(0, newlineIdx);
        embeddedCount = rawLabel.slice(newlineIdx + 1);
    }

    // Non-condition nodes: first line is header, rest is body (e.g. "All Patients\nCount: X")
    const headerText = role !== 'condition' && newlineIdx > -1 ? rawLabel.slice(0, newlineIdx) : null;
    const bodyText = role !== 'condition' && newlineIdx > -1 ? rawLabel.slice(newlineIdx + 1) : mainText;

    const nodeHeight = role === 'condition'
        ? HEADER_HEIGHT + estimateBodyHeight(mainText) + (embeddedCount ? COUNT_SECTION_HEIGHT : 0)
        : NODE_HEIGHTS[role];

    const label = (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <div style={{
                background: c.header,
                color: c.headerText,
                padding: '6px 10px',
                fontWeight: 'bold',
                fontSize: 11,
                textAlign: 'center',
                borderRadius: '10px 10px 0 0',
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                flexShrink: 0,
            }}>
                {headerText || roleLabel(role)}
            </div>
            <div style={{
                background: c.body,
                color: c.bodyText,
                padding: embeddedCount ? '6px 10px 4px' : '8px 10px',
                fontSize: role === 'count' || role === 'final' ? 15 : 12,
                fontWeight: role === 'count' || role === 'final' ? 'bold' : 'normal',
                textAlign: 'center',
                borderRadius: embeddedCount ? '0' : '0 0 10px 10px',
                flexGrow: embeddedCount ? 0 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
            }}>
                {bodyText}
            </div>
            {embeddedCount && (
                <div style={{
                    background: colors.count.body,
                    color: colors.count.bodyText,
                    padding: '4px 10px',
                    fontSize: 13,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    borderRadius: '0 0 10px 10px',
                    borderTop: `1px solid ${colors.count.border}`,
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {embeddedCount}
                </div>
            )}
        </div>
    );

    return {
        ...node,
        data: { ...node.data, label },
        style: {
            background: 'transparent',
            border: `2px solid ${c.border}`,
            borderRadius: 12,
            padding: 0,
            width: NODE_WIDTH,
            height: nodeHeight,
            boxShadow: colors.nodeShadow,
            overflow: 'hidden',
        },
    };
};

const roleLabel = (role) => {
    if (role === 'root') return 'All Patients';
    if (role === 'condition') return 'Filter';
    if (role === 'count') return 'Patient Count';
    return 'Final Result';
};

// ─── dagre auto-layout ────────────────────────────────────────────────────────
// Direction: TB (top→bottom). Dagre spreads siblings horizontally
// when a node has multiple children, giving a natural tree structure.

const applyDagreLayout = (nodes, edges) => {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
        rankdir: 'TB',
        ranksep: 60,   // vertical space between ranks
        nodesep: 40,   // horizontal space between sibling nodes
        edgesep: 20,
    });

    nodes.forEach(node => {
        const role = detectRole(node);
        const height = node.style?.height || NODE_HEIGHTS[role];
        g.setNode(node.id, { width: NODE_WIDTH, height });
    });

    edges.forEach(edge => g.setEdge(edge.source, edge.target));

    dagre.layout(g);

    const layoutedNodes = nodes.map(node => {
        const role = detectRole(node);
        const height = node.style?.height || NODE_HEIGHTS[role];
        const { x, y } = g.node(node.id);
        return {
            ...node,
            position: {
                x: x - NODE_WIDTH / 2,
                y: y - height / 2,
            },
        };
    });

    return layoutedNodes;
};

// ─── OR-query graph fix ───────────────────────────────────────────────────────
//
// Problem the agent sometimes produces for OR queries:
//   root → combined_filter → final        (wrong: final hangs off a condition)
//   root → condition_a → count_a          (counts are orphaned from final)
//   root → condition_b → count_b
//
// Desired structure:
//   root → condition_a → count_a → final  (counts converge at final)
//   root → condition_b → count_b ↗
//
// Detection: final node receives an edge FROM a condition node (not a count).
// Fix: remove the redundant combined-condition node and wire orphaned counts → final.

const fixOrQueryStructure = (nodes, edges, colors) => {
    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

    const finalNode = nodes.find(n => n.type === 'output');
    if (!finalNode) return { nodes, edges };

    // Find edges that feed INTO the final node from a condition (not a count)
    const badEdges = edges.filter(e => {
        if (e.target !== finalNode.id) return false;
        const src = nodeMap[e.source];
        return src && detectRole(src) === 'condition';
    });

    if (badEdges.length === 0) return { nodes, edges }; // already correct

    const badNodeIds = new Set(badEdges.map(e => e.source));

    // Count nodes that currently have NO edge going to the final node
    const orphanedCounts = nodes.filter(n => {
        if (detectRole(n) !== 'count') return false;
        return !edges.some(e => e.source === n.id && e.target === finalNode.id);
    });

    // Remove bad nodes and all their edges
    const cleanedNodes = nodes.filter(n => !badNodeIds.has(n.id));
    const cleanedEdges = edges.filter(e =>
        !badNodeIds.has(e.source) && !badNodeIds.has(e.target)
    );

    // Wire orphaned count nodes → final
    const fixedEdges = [
        ...cleanedEdges,
        ...orphanedCounts.map((n, i) => ({
            id: `e-fix-${n.id}-final-${i}`,
            source: n.id,
            target: finalNode.id,
            type: 'smoothstep',
            animated: true,
            style: { stroke: colors.edge, strokeWidth: 2 },
        })),
    ];

    return { nodes: cleanedNodes, edges: fixedEdges };
};

// ─── merge count nodes into their condition parents ───────────────────────────
//
// Eliminates separate "Patient Count" nodes by appending the count text onto
// the label of the condition node directly above it.  Only count nodes whose
// immediate parent is a condition node are merged; union-count nodes (whose
// parents are other counts) and the final node are left untouched.

const mergeCountsIntoConditions = (nodes, edges) => {
    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
    const toRemove = new Set();
    const labelAdditions = {};

    nodes.forEach(node => {
        if (detectRole(node) !== 'count') return;

        const inEdge = edges.find(e => e.target === node.id);
        if (!inEdge) return;

        const parent = nodeMap[inEdge.source];
        if (!parent || detectRole(parent) !== 'condition') return;

        // Normalise "Count: 1,234" → keep the full string as the count sub-label
        const countText = String(node.data?.label || '').trim();
        labelAdditions[parent.id] = countText;
        toRemove.add(node.id);
    });

    // Update condition node labels
    const updatedNodes = nodes
        .filter(n => !toRemove.has(n.id))
        .map(n => {
            const addition = labelAdditions[n.id];
            if (!addition) return n;
            return {
                ...n,
                data: { ...n.data, label: String(n.data?.label || '') + '\n' + addition },
            };
        });

    // Rewire edges: condition → (what the count node was pointing to)
    const removedEdgeIds = new Set();
    const addedEdges = [];

    edges.forEach(edge => {
        if (toRemove.has(edge.target)) {
            // edge INTO a removed count node — drop it
            removedEdgeIds.add(edge.id);
        } else if (toRemove.has(edge.source)) {
            // edge OUT OF a removed count node — rewire to its condition parent
            removedEdgeIds.add(edge.id);
            const inEdge = edges.find(e => e.target === edge.source);
            if (inEdge) {
                addedEdges.push({ ...edge, id: `${edge.id}-rewired`, source: inEdge.source });
            }
        }
    });

    const updatedEdges = [
        ...edges.filter(e => !removedEdgeIds.has(e.id)),
        ...addedEdges,
    ];

    return { nodes: updatedNodes, edges: updatedEdges };
};

// ─── main parse + transform ───────────────────────────────────────────────────

const processAgentFlow = (reactflowJson, cohortLogic, colors) => {
    if (!reactflowJson && !cohortLogic) return { nodes: [], edges: [] };

    let raw;
    let isCompactFlow = false;
    try {
        if (cohortLogic) {
            const parsedCohortLogic = typeof cohortLogic === 'string'
                ? JSON.parse(cohortLogic)
                : cohortLogic;
            raw = cohortLogicToReactFlow(parsedCohortLogic);
            isCompactFlow = raw.nodes.length > 0;
        }

        // Keep legacy reactflow_json as a rollout fallback if compact data is
        // absent or invalid.
        if (!isCompactFlow && reactflowJson) {
            raw = typeof reactflowJson === 'string' ? JSON.parse(reactflowJson) : reactflowJson;
        }
    } catch (e) {
        console.error('AgentFlowLogic: failed to parse cohort flow data', e);
        return { nodes: [], edges: [] };
    }

    const rawNodes = raw?.nodes || [];
    const rawEdges = raw?.edges || [];

    // Compact cohort_logic is already structurally correct and embeds each count
    // in its semantic node. Legacy diagrams still need the historical repair and
    // count-node merge passes.
    const { nodes: mergedNodes, edges: mergedEdges } = isCompactFlow
        ? { nodes: rawNodes, edges: rawEdges }
        : (() => {
            const fixed = fixOrQueryStructure(rawNodes, rawEdges, colors);
            return mergeCountsIntoConditions(fixed.nodes, fixed.edges);
        })();

    // Apply styles, then let dagre calculate positions
    const styledNodes = mergedNodes.map((n) => buildStyledNode(n, colors));

    const styledEdges = mergedEdges.map(edge => ({
        ...edge,
        type: 'smoothstep',
        animated: true,
        style: { stroke: colors.edge, strokeWidth: 2 },
        labelStyle: { fontSize: 11, fontWeight: 'bold', fill: colors.edgeLabel },
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
    }));

    const layoutedNodes = applyDagreLayout(styledNodes, styledEdges);

    return { nodes: layoutedNodes, edges: styledEdges };
};

// ─── inner ReactFlow canvas ───────────────────────────────────────────────────

const AgentFlowLogicInner = ({ nodes, edges, onInitHandler }) => {
    const { getNodes } = useReactFlow();
    const [isExporting, setIsExporting] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const captureImage = async () => {
        const viewportEl = document.querySelector('.react-flow__viewport');
        if (!viewportEl) throw new Error('Viewport not found');

        const currentNodes = getNodes();
        if (!currentNodes?.length) throw new Error('No nodes');

        const nodeEls = viewportEl.querySelectorAll('.react-flow__node');
        const saved = [];
        nodeEls.forEach(el => {
            saved.push({ el, height: el.style.height, overflow: el.style.overflow });
            el.style.height = 'auto';
            el.style.overflow = 'visible';
        });

        await new Promise(r => setTimeout(r, 50));

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        currentNodes.forEach((node, i) => {
            const el = nodeEls[i];
            const w = el ? el.offsetWidth : NODE_WIDTH;
            const h = el ? el.offsetHeight : 90;
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + w);
            maxY = Math.max(maxY, node.position.y + h);
        });

        const pad = 80;
        const imgW = (maxX - minX) + pad * 2;
        const imgH = (maxY - minY) + pad * 2;

        try {
            const dataUrl = await toPng(viewportEl, {
                // Exported images stay light regardless of the UI mode.
                backgroundColor: '#f8fafc',
                width: imgW,
                height: imgH,
                style: {
                    width: `${imgW}px`,
                    height: `${imgH}px`,
                    transform: `translate(${pad - minX}px, ${pad - minY}px) scale(1)`,
                },
            });
            return { dataUrl, imgW, imgH };
        } finally {
            saved.forEach(({ el, height, overflow }) => {
                el.style.height = height;
                el.style.overflow = overflow;
            });
        }
    };

    const handleDownloadPNG = async () => {
        setIsExporting(true);
        try {
            const { dataUrl } = await captureImage();
            const link = document.createElement('a');
            link.setAttribute('download', 'agent-query-logic.png');
            link.setAttribute('href', dataUrl);
            link.click();
        } catch (err) {
            console.error('PNG export error:', err);
        } finally {
            setIsExporting(false);
        }
    };

    const handleDownloadPDF = async () => {
        setIsExporting(true);
        try {
            const { dataUrl, imgW, imgH } = await captureImage();
            const pdf = new jsPDF(imgW > imgH ? 'landscape' : 'portrait', 'px', [imgW, imgH]);
            pdf.addImage(dataUrl, 'PNG', 0, 0, imgW, imgH);
            pdf.save('agent-query-logic.pdf');
        } catch (err) {
            console.error('PDF export error:', err);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onInit={onInitHandler}
            fitView
            fitViewOptions={{ padding: 0.3, includeHiddenNodes: true }}
        >
            <Controls />
            <Panel position="top-right">
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={isExporting ? <CircularProgress size={16} color="primary" /> : <DownloadIcon />}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    disabled={isExporting || !nodes?.length}
                    sx={{
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        backgroundColor: 'background.paper',
                        textTransform: 'none',
                        '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(4,174,234,0.12)' },
                    }}
                >
                    Download
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => { setAnchorEl(null); handleDownloadPNG(); }}>
                        <ListItemIcon><ImageIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Download as PNG</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); handleDownloadPDF(); }}>
                        <ListItemIcon><PictureAsPdfIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Download as PDF</ListItemText>
                    </MenuItem>
                </Menu>
            </Panel>
        </ReactFlow>
    );
};

// ─── SQL fallback panel ───────────────────────────────────────────────────────

const SqlFallback = ({ sqlStatement }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(sqlStatement).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ width: '100%', height: '80vh', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Generated SQL Query
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy SQL'} placement="left">
                    <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? 'success.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box
                component="pre"
                sx={{
                    flexGrow: 1,
                    m: 0,
                    p: 2,
                    bgcolor: '#0f172a',
                    color: '#e2e8f0',
                    borderRadius: '10px',
                    fontSize: '0.78rem',
                    fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
                    lineHeight: 1.65,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    border: '1px solid #1e293b',
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)',
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { bgcolor: '#1e293b', borderRadius: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#334155', borderRadius: 4 },
                }}
            >
                {sqlStatement}
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
                No flow diagram was generated for this query. Ask a data question in the chat to generate one.
            </Typography>
        </Box>
    );
};

// ─── public component ─────────────────────────────────────────────────────────

const AgentFlowLogic = ({ reactflowJson, cohortLogic, sqlStatement }) => {
    const reactFlowInstanceRef = useRef(null);
    const theme = useTheme();
    const colors = nodeColors(theme);

    const { nodes: initNodes, edges: initEdges } = processAgentFlow(reactflowJson, cohortLogic, colors);
    const [nodes, setNodes] = useNodesState(initNodes);
    const [edges, setEdges] = useEdgesState(initEdges);

    useEffect(() => {
        const { nodes: newNodes, edges: newEdges } = processAgentFlow(reactflowJson, cohortLogic, nodeColors(theme));
        setNodes(newNodes);
        setEdges(newEdges);
    }, [reactflowJson, cohortLogic, setNodes, setEdges, theme]);

    useEffect(() => {
        if (nodes.length > 0 && reactFlowInstanceRef.current) {
            setTimeout(() => {
                reactFlowInstanceRef.current.fitView({ padding: 0.3, includeHiddenNodes: true });
            }, 300);
        }
    }, [nodes.length]);

    if ((!reactflowJson && !cohortLogic) || nodes.length === 0) {
        if (sqlStatement) {
            return <SqlFallback sqlStatement={sqlStatement} />;
        }
        return (
            <Box sx={{ width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
                    <Typography variant="body1" color="text.secondary">
                        Ask a question in the chat to generate a query logic diagram.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '80vh', position: 'relative' }}>
            <ReactFlowProvider>
                <AgentFlowLogicInner
                    nodes={nodes}
                    edges={edges}
                    onInitHandler={(instance) => { reactFlowInstanceRef.current = instance; }}
                />
            </ReactFlowProvider>
        </Box>
    );
};

export default AgentFlowLogic;
